import { ArrayQueue, type Backoff, type Websocket, WebsocketBuilder } from "websocket-ts";
import { Observable } from "rxjs";
import { jwtDecode } from "jwt-decode";
import { logger } from "@artis/logger";

// Server sends a keep-alive at least every 500ms; this much silence means a zombie socket.
const WATCHDOG_MS = 5_000;

const isFresh = (token: string, bufferSec = 60): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return typeof exp === "number" && exp - Math.floor(Date.now() / 1000) > bufferSec;
  } catch {
    return false;
  }
};

export type LivePricesConfig = { wsV2: boolean; traceSampleRate?: number };

const DEFAULT_TRACE_SAMPLE_RATE = 0.1;

/** Derive the worker config from the live-prices-ws-v2 flag; metadata may carry { sampleRate } to tune tracing remotely. */
export const livePricesConfigFromFlag = (flag: { metadata?: unknown } | undefined): LivePricesConfig => {
  if (!flag) return { wsV2: false, traceSampleRate: 0 };
  const metadata = flag.metadata;
  const rate =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? (metadata as { sampleRate?: unknown }).sampleRate
      : undefined;
  const sampleRate = typeof rate === "number" ? rate : DEFAULT_TRACE_SAMPLE_RATE;
  return { wsV2: true, traceSampleRate: Math.min(1, Math.max(0, sampleRate)) };
};

export const resolveLivePricesUrl = (base: string, wsV2: boolean): string => {
  if (!wsV2) return base;
  const url = new URL(base);
  url.pathname = `${url.pathname.replace(/\/$/, "")}/v2`;
  return url.toString();
};

export const createLivePriceWsConnection =
  (url: string) =>
  (token$: Observable<string>, config$?: Observable<LivePricesConfig>): Observable<Uint8Array> => {
    return new Observable<Uint8Array>((observer) => {
      let latestToken: string | null = null;
      let ws: Websocket | null = null;
      let wsV2 = false;

      let watchdog: ReturnType<typeof setTimeout> | undefined;
      const clearWatchdog = () => {
        if (watchdog !== undefined) {
          clearTimeout(watchdog);
          watchdog = undefined;
        }
      };
      const armWatchdog = (instance: Websocket) => {
        clearWatchdog();
        watchdog = setTimeout(() => {
          watchdog = undefined;
          logger.warn("Live-prices watchdog: no message within 5s, forcing reconnect", {
            tags: ["calc-worker"],
          });
          try {
            instance.underlyingWebsocket.close();
          } catch {}
        }, WATCHDOG_MS);
      };

      const connect = () => {
        logger.info("Initializing new LivePrice WebSocket connection", { ws_v2: wsV2 });
        return new WebsocketBuilder(resolveLivePricesUrl(url, wsV2))
          .withBackoff(new InfiniteExponentialBackoff(2_000, 20_000))
          .withBuffer(new ArrayQueue())
          .onOpen((ws, ev) => {
            const nativeWs = ev.target as WebSocket;
            if (nativeWs) {
              nativeWs.binaryType = "arraybuffer";
            }
            const token = latestToken;
            if (!token || !isFresh(token)) {
              let exp: number | undefined;
              let secs_to_exp: number | null = null;
              if (token) {
                try {
                  exp = jwtDecode<{ exp?: number }>(token).exp;
                  if (typeof exp === "number") secs_to_exp = exp - Math.floor(Date.now() / 1000);
                } catch {}
              }
              logger.warn("Live-prices token missing or stale; retrying with backoff until a fresh token arrives", {
                tags: ["auth", "calc-worker"],
                has_token: Boolean(token),
                exp,
                secs_to_exp,
              });
              try {
                ws.underlyingWebsocket.close();
              } catch {}
              return;
            }
            try {
              const { exp } = jwtDecode<{ exp?: number }>(token);
              logger.info("Live-prices ws sending token on open", {
                tags: ["auth", "calc-worker"],
                exp,
                secs_to_exp: typeof exp === "number" ? exp - Math.floor(Date.now() / 1000) : null,
              });
            } catch {}
            ws.send(token);
            armWatchdog(ws);
          })
          .onMessage((instance, ev) => {
            armWatchdog(instance);
            observer.next(ev.data);
          })
          .onError((_, ev) => {
            logger.error("Live prices connection error", ev);
          })
          .onClose((_, ev) => {
            clearWatchdog();
            logger.warn(`Live prices connection closed: ${ev.code} (${reasons[ev.code] ?? `Unknown (${ev.code})`})`);
          })
          .onRetry((_, ev) => {
            logger.warn(
              `Retrying live prices connection (attempt ${ev.detail.retries}, backoff ${ev.detail.backoff}ms)`,
            );
          })
          .onReconnect(() => {
            logger.info("Live prices connection reconnected");
          })
          .build();
      };

      const configSub = config$?.subscribe(({ wsV2: enabled }) => {
        if (enabled === wsV2) return;
        wsV2 = enabled;
        if (ws) {
          logger.info("Live-prices contract version changed, reconnecting", { ws_v2: wsV2 });
          clearWatchdog();
          ws.close();
          ws = connect();
        }
      });

      const tokenSub = token$.subscribe((token) => {
        latestToken = token;
        if (!ws) ws = connect();
      });

      return () => {
        logger.info("Tearing down LivePrice WebSocket connection");
        tokenSub.unsubscribe();
        configSub?.unsubscribe();
        clearWatchdog();
        ws?.close();
      };
    });
  };

const reasons: Record<number, string> = {
  1000: "Normal closure",
  1001: "Going away",
  1002: "Protocol error",
  1003: "Unsupported data",
  1005: "No status received",
  1006: "Abnormal closure",
  1007: "Invalid payload",
  1008: "Policy violation",
  1009: "Message too big",
  1010: "Mandatory extension",
  1011: "Internal server error",
  1015: "TLS handshake failure",
  4400: "Bad request",
  4403: "Forbidden (no exchange permissions)",
  4401: "Token expired",
};

export class InfiniteExponentialBackoff implements Backoff {
  private _retries = 0;
  private _current = 0;

  constructor(
    private readonly base: number = 2000,
    private readonly maxDelay: number = 60000,
  ) {}

  public get retries(): number {
    return this._retries;
  }

  public get current(): number {
    return this._current;
  }

  public next(): number {
    // Calculate delay: base * 2^retries
    const delay = this.base * 2 ** this._retries;

    // Increment retry count
    this._retries++;

    // Set current delay (capped at maxDelay)
    this._current = Math.min(delay, this.maxDelay);

    return this._current;
  }

  public reset(): void {
    this._retries = 0;
    this._current = 0;
  }
}
